import { ReactNode, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { Box, Tooltip, Typography } from "@mui/material";

interface MiStackedTypographyProps {
  title: string;
  description?: string | ReactNode;
  titleProps?: Record<string, unknown>;
  descriptionProps?: Record<string, unknown>;
  tooltipProps?: Record<string, unknown>;
  titleFontSize?: number | string;
  descriptionFontSize?: number | string;
  boldDescription?: boolean;
  forceTooltip?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

interface MiStackedTypographyStyleParams {
  titleFontSize: number | string;
  descriptionFontSize: number | string;
  boldDescription: boolean;
}

export const MiStackedTypography = ({
  title,
  description,
  titleProps,
  descriptionProps,
  tooltipProps,
  titleFontSize = 12,
  descriptionFontSize = 14,
  boldDescription = false,
  forceTooltip = false,
  className,
  style,
  ...rest
}: MiStackedTypographyProps) => {
  const titleRef = useRef<HTMLElement>(null);
  const textRef = useRef<HTMLElement>(null);
  const { classes, theme } = useStyles({
    titleFontSize,
    descriptionFontSize,
    boldDescription,
  });
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  const isElementOverflowing = (el: HTMLElement) =>
    el.scrollWidth > el.clientWidth;

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTextTruncated(isElementOverflowing(textRef.current));
      }
      if (titleRef.current) {
        setIsTitleTruncated(isElementOverflowing(titleRef.current));
      }
    };

    checkTruncation();

    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [description, descriptionProps?.children]);

  const titleContent = (titleProps?.children || title) as ReactNode | string;
  const tooltipContent = (descriptionProps?.children || description) as
    | ReactNode
    | string;

  const TextComponent = (
    <Typography
      noWrap
      ref={textRef}
      variant="subtitle2"
      className={classes.description}
      {...descriptionProps}
    >
      {tooltipContent}
    </Typography>
  );

  const TitleComponent = (
    <Typography
      noWrap
      ref={titleRef}
      className={classes.title}
      color={theme.palette.grey.main}
      {...titleProps}
    >
      {titleContent}
    </Typography>
  );

  return (
    <Box className={className} style={style} {...rest}>
      {isTitleTruncated ? (
        <Tooltip title={titleContent} {...tooltipProps}>
          {TitleComponent}
        </Tooltip>
      ) : (
        TitleComponent
      )}
      {isTextTruncated || forceTooltip ? (
        <Tooltip title={tooltipContent} {...tooltipProps}>
          {TextComponent}
        </Tooltip>
      ) : (
        TextComponent
      )}
    </Box>
  );
};

const useStyles = makeStyles<MiStackedTypographyStyleParams>()(
  (theme, { titleFontSize, descriptionFontSize, boldDescription }) => ({
    title: {
      fontSize: titleFontSize,
    },
    description: {
      fontSize: descriptionFontSize,
      fontWeight: boldDescription ? theme.fontWeight.bold : undefined,
      [theme.breakpoints.down("xl")]: {
        fontSize:
          typeof descriptionFontSize === "number" && descriptionFontSize < 12
            ? descriptionFontSize
            : 12,
      },
    },
  })
);
